import React from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import {
  Resource, ColumnSet, QueryAction, MutationAction,
} from 'ants/resource';
import { arrayAsObject, objectAsArray } from 'lib/utils';
import { isDeletedValue } from 'core/Form';
import {
  PlayerRankingDeleteMutation,
  PlayerRankingReadQuery,
  PlayerRankingUpdateMutation,
  PlayerRankingSearchQuery,
  PlayerRankingTopCountsQuery,
  PlayerRankingCreateMutation,
} from './query';
import { PlayerRankingImageView } from './ResourceImage';

function PlayerPointsValidator() {
  const validateRanks = (_, context) => {
    const { ranks } = context.options.context.initialValues;
    const allRanks = objectAsArray(ranks);
    for (let i = 0; i < allRanks.length; i += 1) {
      if (!isDeletedValue(allRanks[i]) && allRanks.points <= 0) {
        return 'Invalid Points';
      }
    }

    return true;
  };

  return Yup.mixed()
    .test(
      'ranks',
      validateRanks,
    );
}

export const PlayerRankingResource = Resource({
  resourceId: 'PlayerRanking',
  name: 'PlayerRanking',
  storedId: 'PlayerRanking',
  pageKey: 'sports.cricket.PlayerRanking',
  sportScope: false,
  columnSets: [
    ColumnSet({
      name: 'ranks',
      shape: Yup.object().shape({
        ranks: PlayerPointsValidator(),
      }),
      viewKey: 'ranks',
    }),
    ColumnSet({
      name: 'updatedDate',
      shape: Yup.object().shape({
        ranking_release_date: Yup.number().nullable().default(null),
        ranking_recorded_date: Yup.number().nullable().default(null),
      }),
      viewKey: 'updated_date',
      historyKey: 'PlayerRankingUpdatedDate',
    }),

  ],
  listPrimaryActions: [
    { action: 'update', name: 'Update' },
  ],
  renderImage: (item) => (<PlayerRankingImageView item={item} />),
  queries: {
    read: QueryAction({
      query: PlayerRankingReadQuery,
      responsePath: 'sports_cricket_player_ranking_read',
      resourceNamePath: 'item.name.name',
    }),
    search: QueryAction({
      query: PlayerRankingSearchQuery,
      responsePath: 'sports_cricket_player_ranking_search',
    }),
    counts: QueryAction({
      query: PlayerRankingTopCountsQuery,
      responsePath: 'sports_cricket_player_ranking_search_top_counts',
    }),

  },
  mutations: {
    update: MutationAction({
      mutation: PlayerRankingUpdateMutation,
      responsePath: 'sports_cricket_player_ranking_update',
      cs: ['ranks', 'updated_date'],
      prepareForEdit(self, resource, data) {
        const resp = self.defaultPrepareForEdit(self, resource, data);
        resp.ranks = arrayAsObject(resp.ranks) || {};
        // console.log('prepareforedit', resp);
        return resp;
      },
      prepareForSave(self, resource, data) {
        // console.log('prepareforsave', data);
        const resp = self.defaultPrepareForSave(self, resource, data);
        resp.ranks = objectAsArray(resp.ranks) || [];
        resp.ranks = resp?.ranks?.filter((r) => r.player && r.player.hashkey);
        resp.ranks.sort((a, b) => b.points - a.points);

        resp.updated_date.ranking_recorded_date = moment().set({
          hour: 0, minute: 0, second: 0, millisecond: 0,
        }).unix();

        if (!resp?.updated_date?.ranking_release_date) {
          resp.updated_date.ranking_release_date = moment().set({
            hour: 0, minute: 0, second: 0, millisecond: 0,
          }).unix();
        }
        let rank = 1;
        for (let i = 0; i < resp.ranks.length; i += 1) {
          if (i > 0) {
            if (resp.ranks[i].points < resp.ranks[i - 1].points) {
              rank = i + 1;
            }
          }
          resp.ranks[i].player = {
            _hashkey: resp.ranks[i].player.hashkey,
          };
          resp.ranks[i].team = {
            _hashkey: resp.ranks[i].team.hashkey,
          };
          resp.ranks[i].ranking = rank;
          resp.ranks[i].points = parseFloat(resp.ranks[i].points, 2);
          delete resp.ranks[i].position;
          delete resp.ranks[i].changed_by;
        }

        return resp;
      },
    }),
    delete: MutationAction({
      mutation: PlayerRankingDeleteMutation,
      responsePath: 'sports_cricket_player_ranking_delete',
    }),
    create: MutationAction({
      mutation: PlayerRankingCreateMutation,
      responsePath: 'sports_cricket_player_ranking_create',
    }),
  },
});

export default PlayerRankingResource;
